import { useCallback, useState } from 'react';

import { getDocumentTypes } from '../api/index';
import { ConstructorApi } from '../api';
import { queryClient } from '@/app/QueryClientProvider';
import { usePopups } from '@/hooks';

export const useDocumentType = ({ kycDocumentSectionId }) => {
  const { showErrorToast, showSuccessToast } = usePopups();
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isChangingRole, setIsChangingRole] = useState(false);
  const [isChangingIsRequired, setIsChangingIsRequired] = useState(false);
  const handleCreate = useCallback(async (section) => {
    try {
      setIsCreating(true);
      const res = await ConstructorApi.createDocumentType(section, kycDocumentSectionId);
      await queryClient.refetchQueries({ queryFn: () => getDocumentTypes(kycDocumentSectionId) });
      showSuccessToast('Created');
      return res;
    } catch (e) {
      showErrorToast(e, true);
    } finally {
      setIsCreating(false);
    }
  }, []);

  const handleUpdate = useCallback(async (section) => {
    try {
      setIsUpdating(true);
      await ConstructorApi.updateDocumentType(section);
      await queryClient.refetchQueries({ queryFn: () => getDocumentTypes(kycDocumentSectionId) });
      showSuccessToast('Saved');
    } catch (e) {
      showErrorToast(e, true);
    } finally {
      setIsUpdating(false);
    }
  }, []);

  const handleDelete = useCallback(async (type) => {
    try {
      setIsDeleting(true);
      await ConstructorApi.deleteDocumentType(type);
      await queryClient.refetchQueries({ queryFn: () => getDocumentTypes(kycDocumentSectionId) });
      showSuccessToast('Deleted');
    } catch (e) {
      showErrorToast(e, true);
    } finally {
      setIsDeleting(false);
    }
  }, []);

  const handleChangeRole = useCallback(async (role, checked, section) => {
    try {
      setIsChangingRole(true);
      const isRequired = false;
      if (checked) {
        await ConstructorApi.addRoleDocumentType(section.id, role, isRequired);
      } else {
        await ConstructorApi.deleteRoleDocumentType(section.id, role);
      }
      await queryClient.refetchQueries({
        queryFn: () => getDocumentTypes(kycDocumentSectionId)
      });

      if (checked) {
        showSuccessToast('Role Added');
      } else {
        showSuccessToast('Role Removed');
      }
    } catch (e) {
      showErrorToast(e, true);
    } finally {
      setIsChangingRole(false);
    }
  }, []);

  const handleChangeIsRequired = useCallback(async (role, checked, section) => {
    try {
      setIsChangingIsRequired(true);
      let isRequired = checked;

      await ConstructorApi.addRoleDocumentType(section.id, role, isRequired);

      await queryClient.refetchQueries({
        queryFn: () => getDocumentTypes(kycDocumentSectionId)
      });

      if (checked) {
        showSuccessToast('Is Required ' + role);
      } else {
        showSuccessToast('Not Required ' + role);
      }
    } catch (e) {
      showErrorToast(e, true);
    } finally {
      setIsChangingIsRequired(false);
    }
  }, []);

  return {
    handleCreate,
    handleUpdate,
    handleDelete,
    handleChangeRole,
    handleChangeIsRequired,
    isCreating,
    isUpdating,
    isDeleting,
    isChangingRole,
    isChangingIsRequired
  };
};
